import { useState, useEffect } from 'react';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "@/components/ui/avatar";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useUsuarios } from '@/hooks/useUsuarios';
import { useAuth } from '@/contexts/AuthContext';
import { useOnlinePresence } from '@/hooks/useOnlinePresence';
import { QuickChatPanel } from '@/components/chat/QuickChatPanel';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';

const MAX_VISIBLE_AVATARS = 3;

// Função para obter as iniciais do nome
const getInitials = (name?: string): string => {
    if (!name) return 'U';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return 'U';
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

interface SelectedUser {
    id: string;
    name: string;
    avatarUrl?: string;
}

export function OnlineUsersAvatars() {
    const { data: usuarios, isLoading } = useUsuarios();
    const { user: currentUser } = useAuth();
    const { getUnreadCount } = useUnreadMessages();
    const { onlineUserIds } = useOnlinePresence();

    // Initialize from localStorage
    const [openChats, setOpenChats] = useState<SelectedUser[]>(() => {
        const saved = localStorage.getItem('open_chats');
        return saved ? JSON.parse(saved) : [];
    });

    // Save to localStorage whenever openChats changes
    useEffect(() => {
        localStorage.setItem('open_chats', JSON.stringify(openChats));
    }, [openChats]);

    // Filtrar usuários - exclui o usuário logado e mantém apenas online
    const onlineUsers = (usuarios || []).filter(user => {
        // Exclui o próprio usuário logado
        if (currentUser && user.id === currentUser.id) return false;
        // Check real online status
        return onlineUserIds.has(user.id);
    });

    const visibleUsers = onlineUsers.slice(0, MAX_VISIBLE_AVATARS);
    const remainingCount = onlineUsers.length - MAX_VISIBLE_AVATARS;
    const remainingUsers = onlineUsers.slice(MAX_VISIBLE_AVATARS);

    const handleAvatarClick = (user: typeof onlineUsers[0]) => {
        const displayName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'Usuário';
        const avatarUrl = user.user_metadata?.avatar_url;

        // Verify if user is already open
        const isAlreadyOpen = openChats.some(chat => chat.id === user.id);

        if (!isAlreadyOpen) {
            // Limit to 3 open chats to avoid clutter
            if (openChats.length >= 3) {
                // Remove the oldest chat (first in array) and add the new one
                setOpenChats(prev => [...prev.slice(1), {
                    id: user.id,
                    name: displayName,
                    avatarUrl: avatarUrl || undefined,
                }]);
            } else {
                setOpenChats(prev => [...prev, {
                    id: user.id,
                    name: displayName,
                    avatarUrl: avatarUrl || undefined,
                }]);
            }
        }
    };

    const handleCloseChat = (userId: string) => {
        setOpenChats(prev => prev.filter(chat => chat.id !== userId));
    };

    if (isLoading) {
        return (
            <div className="flex items-center">
                <div className="-space-x-2 flex">
                    {[1, 2, 3].map((i) => (
                        <Avatar key={i} className="h-8 w-8 ring-2 ring-background">
                            <AvatarFallback className="bg-muted animate-pulse" />
                        </Avatar>
                    ))}
                </div>
            </div>
        );
    }

    console.log('OnlineUsersAvatars Debug:', {
        totalUsers: usuarios?.length,
        onlineCount: onlineUserIds.size,
        onlineUsersFiltered: onlineUsers.length,
        currentUser: currentUser?.id
    });

    if (onlineUsers.length === 0) {
        return (
            <div className="flex items-center text-xs text-muted-foreground px-2">
                <span className="h-2 w-2 rounded-full bg-slate-300 mr-2" />
                Ninguém online
            </div>
        );
    }

    return (
        <>
            <TooltipProvider>
                <div className="flex items-center">
                    <div className="group -space-x-2 flex">
                        {visibleUsers.map((user) => {
                            const displayName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'Usuário';
                            const avatarUrl = user.user_metadata?.avatar_url;
                            const initials = getInitials(displayName);
                            const unreadCount = getUnreadCount(user.id);
                            const isOpen = openChats.some(chat => chat.id === user.id);

                            return (
                                <Tooltip key={user.id}>
                                    <TooltipTrigger asChild>
                                        <div className="relative">
                                            <Avatar
                                                className={`h-8 w-8 ring-2 cursor-pointer transition-all duration-200 
                                                    ${isOpen ? 'ring-green-500 scale-110 blur-0 opacity-100' : 'ring-background'}
                                                    group-hover:blur-[1px] group-hover:opacity-70 hover:!blur-none hover:!opacity-100 hover:scale-110 hover:z-10`}
                                                onClick={() => handleAvatarClick(user)}
                                            >
                                                {avatarUrl ? (
                                                    <AvatarImage
                                                        alt={displayName}
                                                        src={avatarUrl}
                                                    />
                                                ) : null}
                                                <AvatarFallback className="text-xs bg-primary/10 text-primary font-medium">
                                                    {initials}
                                                </AvatarFallback>
                                            </Avatar>
                                            {unreadCount > 0 && (
                                                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-background z-20 pointer-events-none">
                                                    {unreadCount > 9 ? '9+' : unreadCount}
                                                </span>
                                            )}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom">
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-2">
                                                <span className="h-2 w-2 rounded-full bg-green-500" />
                                                <span>{displayName}</span>
                                            </div>
                                            {unreadCount > 0 && (
                                                <span className="text-[10px] font-medium text-red-500">
                                                    {unreadCount} mensagem{unreadCount > 1 ? 's' : ''} não lida{unreadCount > 1 ? 's' : ''}
                                                </span>
                                            )}
                                            <span className="text-[10px] text-muted-foreground">Clique para {isOpen ? 'focar' : 'abrir'} chat</span>
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            );
                        })}

                        {remainingCount > 0 && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Avatar className="h-8 w-8 ring-2 ring-background cursor-pointer transition-all duration-200 group-hover:blur-[1px] group-hover:opacity-70 hover:!blur-none hover:!opacity-100 hover:scale-110 hover:z-10">
                                        <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                                            +{remainingCount}
                                        </AvatarFallback>
                                    </Avatar>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    <div className="flex flex-col gap-1">
                                        <span className="font-medium text-xs text-muted-foreground mb-1">
                                            Mais {remainingCount} usuário{remainingCount > 1 ? 's' : ''}:
                                        </span>
                                        {remainingUsers.map((user) => {
                                            const displayName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'Usuário';
                                            const unreadCount = getUnreadCount(user.id);

                                            return (
                                                <div
                                                    key={user.id}
                                                    className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-1 rounded-sm -mx-1 justify-between"
                                                    onClick={() => handleAvatarClick(user)}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <span className="h-2 w-2 rounded-full bg-green-500" />
                                                        <span>{displayName}</span>
                                                    </div>
                                                    {unreadCount > 0 && (
                                                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                                                            {unreadCount}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </div>

                    <span className="ml-2 text-xs text-muted-foreground hidden sm:inline">
                        {onlineUsers.length} usuário{onlineUsers.length > 1 ? 's' : ''}
                    </span>
                </div>
            </TooltipProvider>

            {/* Quick Chat Panel(s) */}
            {openChats.map((chatUser, index) => (
                <QuickChatPanel
                    key={chatUser.id}
                    targetUser={chatUser}
                    onClose={() => handleCloseChat(chatUser.id)}
                    isOpen={true}
                    index={index}
                />
            ))}
        </>
    );
}
